/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import * as Joi from 'joi-browser';

import { withStyles } from '@material-ui/core/styles';

import Form from '../../../common/Form';
import { deepObjectExtend } from '../../../utils/object';
import { combineStyle } from '../../../style/combineStyle';
import { form as FormStyle } from '../../../style/form';
import { DesktopChangePasswordStyle } from '../../../style/DesktopChangePasswordStyle';
import ErrorInline from '../../../common/ErrorMessages/ErrorInline';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import configENV from '../../../config';

const { PERSIST_KEY } = configENV;
const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        FormStyle,
        DesktopChangePasswordStyle,
    }),
);

class DesktopChangePassword extends Form {
    state = {
        data: {
            currentPassword: '',
            newPassword: '',
        },
        showPassword: {
            currentPassword: false,
            newPassword: false,
        },
        isSubmited: false,
        errors: {},
    };

    schema = {
        currentPassword: Joi.string()
            .trim()
            .regex(
                /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{6,}$/,
                'password condition at least 6 characters long with 1 number and 1 letter',
            )
            .min(6)
            .max(50)
            .required()
            .label('Current Password'),
        newPassword: Joi.string()
            .trim()
            .invalid(Joi.ref('currentPassword'))
            .regex(
                /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#*$])[A-Za-z\d$&+,:;=?@#|'<>.^*()%!-{}[\]]{8,}$/,
                'Password must be at least 8 characters long with a mix of uppercase, lowercase, numbers and symbols.',
            )
            .min(8)
            .max(50)
            .required()
            .label('New Password'),
    };

    componentDidMount = () => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (props) => {
        const { isSubmited, data, errors } = this.state;
        const { isReadyState, buttonType } = props;
        if (buttonType === 'SubmitProfile' && data.currentPassword) {
            if (this.validate(true)) {
                this.doSubmit();
            } else {
                const { newPassword } = this.validate();
                if (newPassword) {
                    errors.newPassword = newPassword;
                }
            }
        }
        // Reset password fields
        if (isSubmited && isReadyState && buttonType === '') {
            data.currentPassword = '';
            data.newPassword = '';
            this.setState({ isSubmited: false, data });
        }
    };

    doSubmit = () => {
        const { data } = this.state;
        const { handleUpdatePassword } = this.props;
        // console.log(data);
        this.setState({ isSubmited: true }, () => {
            handleUpdatePassword(data);
        });
    };

    handleNewPasswordChange = (dataChanged) => {
        // eslint-disable-next-line react/jsx-props-no-spreading
        const data = { ...dataChanged };
        if (data.currentPassword === data.newPassword) {
            const errors = {
                newPassword:
                    'New password cannot be the same as current password.',
            };
            this.setState({ errors });
        }
    };

    handleContinueShopping = () => {
        this.props.history.push('/');
    };

    updatePasswordForm = (props) => {
        // eslint-disable-next-line object-curly-newline
        const { isReadyState, classes } = props;
        const submitButtonLabel = isReadyState
            ? 'Update Password'
            : 'Updating...';
        return (
            <>
                <form
                    onSubmit={(event) => this.handleSubmit(event, {})}
                    className={classes.form}
                    disabled={!isReadyState}
                >
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <ErrorInline {...props} name="updatePassword" />
                    <div className="split passwordFields">
                        {this.renderInput({
                            name: 'currentPassword',
                            dataTest: { field: 'my-account-current-password' },
                            label: '',
                            placeholder: '- Current Password -',
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                            desktop: true,
                        })}
                        {this.renderInput({
                            name: 'newPassword',
                            label: '',
                            placeholder: '- New Password -',
                            dataTest: { field: 'my-account-new-password' },
                            type: 'password',
                            length: 50,
                            adornment: 'Show',
                            adornmentPosition: 'end',
                            onChangeHandler: this.handleNewPasswordChange,
                            desktop: true,
                        })}
                        <div className="contReqInfo">
                            <span className="passwordRequirementInfo">At least 8 characters long with a mix of uppercase, <br />lowercase, numbers and symbols.</span>
                        </div>
                    </div>
                    {/* Hide the submit button for the automation test */}
                    <div className="display-none">
                        {this.renderButton({
                            label: submitButtonLabel,
                            className: 'buttonHD',
                            dataTest: { field: 'my-account-submit-password' },
                            onClick: null,
                            type: 'submit',
                            isValidated: true,
                            isReadyState,
                        })}
                    </div>
                </form>
            </>
        );
    };

    successMessageTemplate = (successMessage) => (
        <>
            <div data-test="my-account-change-password-success-text" className="changePasswordSuccess">{successMessage}</div>
        </>
    );

    render() {
        // eslint-disable-next-line object-curly-newline
        const { status, classes, template, successMessage } = this.props;
        return (
            <div className={classes.DesktopChangePasswordStyle}>
                {status !== 'success' ? (
                    <p className="description">
                        To change your password, you must first confirm your
                        current password. Once correct, then enter your new
                        password.
                    </p>
                ) : (
                    ''
                )}
                {status === 'success' && template !== 'updateFirstNameAndLastName' ? this.successMessageTemplate(successMessage) : ''}
                {this.updatePasswordForm(this.props)}
            </div>
        );
    }
}

DesktopChangePassword.defaultProps = {
    status: '',
    template: '',
    successMessage: '',
    buttonType: '',
    error: {},
    handleUpdatePassword: () => { },
};

DesktopChangePassword.propTypes = {
    classes: PropTypes.object.isRequired,
    error: PropTypes.object,
    isReadyState: PropTypes.bool.isRequired,
    status: PropTypes.string,
    buttonType: PropTypes.string,
    template: PropTypes.string,
    successMessage: PropTypes.string,
    handleUpdatePassword: PropTypes.func,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isReadyState: newState.isReadyState,
        status: newState.status,
        template: newState.template,
        buttonType: newState.buttonType,
        successMessage: newState.successMessage,
        error: newState.error,
        toggleState: newState.toggleState,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleUpdatePassword: (data) => dispatch(EventDispatcher.handleUpdatePassword(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles)(DesktopChangePassword)));
